/**
 * Connection Health Monitoring Service
 * Detects when the backend is unreachable and provides offline/online state management
 * Prevents console spam by tracking connection health and pausing requests when offline
 */

import Logger from '../utils/logger.js';

class ConnectionHealthService {
  constructor() {
    this.isOnline = true;
    this.lastHealthCheck = null;
    this.consecutiveFailures = 0;
    this.failureThreshold = 3; // Mark as offline after 3 consecutive failures
    this.healthCheckInterval = null;
    this.listeners = new Set();
    this.offlineSince = null;

    // Bind methods
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);

    // Listen to browser online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  /**
   * Record a successful API call
   */
  recordSuccess() {
    const wasOffline = !this.isOnline;
    this.consecutiveFailures = 0;
    this.isOnline = true;
    this.lastHealthCheck = Date.now();

    if (wasOffline) {
      this.offlineSince = null;
      Logger.info('[CONNECTION HEALTH] Connection restored', {
        offlineDuration: wasOffline && this.offlineSince ? Date.now() - this.offlineSince : 0
      });
      this.notifyListeners({ isOnline: true, wasOffline: true });
    }
  }

  /**
   * Record a failed API call
   * @param {Error} error - The error from the failed request
   * @returns {boolean} - True if connection is still considered online
   */
  recordFailure(error) {
    const isNetworkError = this.isNetworkError(error);

    if (!isNetworkError) {
      // Not a network error (e.g., 401, 403, 404), don't count against health
      return this.isOnline;
    }

    this.consecutiveFailures++;
    this.lastHealthCheck = Date.now();

    // Mark as offline if we hit the threshold
    if (this.consecutiveFailures >= this.failureThreshold && this.isOnline) {
      this.isOnline = false;
      this.offlineSince = Date.now();

      // Only log once when transitioning to offline
      Logger.error('[CONNECTION HEALTH] Backend appears to be offline', {
        consecutiveFailures: this.consecutiveFailures,
        error: error.message
      });

      this.notifyListeners({ isOnline: false, wasOnline: true });
    }

    return this.isOnline;
  }

  /**
   * Check if an error is a network/connection error
   * @param {Error} error - The error to check
   * @returns {boolean}
   */
  isNetworkError(error) {
    // Network errors
    if (
      error.code === 'ECONNREFUSED' ||
      error.code === 'ETIMEDOUT' ||
      error.code === 'ENOTFOUND' ||
      error.code === 'ECONNRESET'
    ) {
      return true;
    }

    // Axios network errors
    if (error.message?.toLowerCase().includes('network error')) {
      return true;
    }

    // Failed to fetch
    if (error.message?.toLowerCase().includes('failed to fetch')) {
      return true;
    }

    // 5xx errors might indicate backend is down
    if (error.response?.status >= 500 && error.response?.status < 600) {
      return true;
    }

    // No response from server
    if (!error.response && error.request) {
      return true;
    }

    return false;
  }

  /**
   * Check if requests should be paused
   * @returns {boolean}
   */
  shouldPauseRequests() {
    return !this.isOnline;
  }

  /**
   * Get current connection status
   * @returns {Object}
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      consecutiveFailures: this.consecutiveFailures,
      lastHealthCheck: this.lastHealthCheck,
      offlineSince: this.offlineSince,
      offlineDuration: this.offlineSince ? Date.now() - this.offlineSince : 0
    };
  }

  /**
   * Subscribe to connection status changes
   * @param {Function} callback - Called when status changes
   * @returns {Function} - Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of status change
   * @param {Object} status - Status change details
   */
  notifyListeners(status) {
    this.listeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        Logger.error('[CONNECTION HEALTH] Error in listener:', error);
      }
    });
  }

  /**
   * Handle browser online event
   */
  handleOnline() {
    Logger.info('[CONNECTION HEALTH] Browser online event detected');
    // Reset failures but let the next successful API call confirm we're online
    this.consecutiveFailures = 0;
  }

  /**
   * Handle browser offline event
   */
  handleOffline() {
    Logger.warn('[CONNECTION HEALTH] Browser offline event detected');
    const wasOnline = this.isOnline;
    this.isOnline = false;
    this.offlineSince = Date.now();

    if (wasOnline) {
      this.notifyListeners({ isOnline: false, wasOnline: true });
    }
  }

  /**
   * Manually reset the connection state
   */
  reset() {
    this.isOnline = true;
    this.consecutiveFailures = 0;
    this.offlineSince = null;
    Logger.info('[CONNECTION HEALTH] Connection state reset');
    this.notifyListeners({ isOnline: true, wasOffline: false });
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }

    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.listeners.clear();
  }
}

// Export singleton instance
export const connectionHealthService = new ConnectionHealthService();

export default connectionHealthService;
